import * as PropTypes from 'prop-types';
import React from 'react';

import {MaybeArchivableItem} from 'archive/ArchiveConstants';
import getArchiveAwareDisplayName from 'archive/getArchiveAwareDisplayName';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import SelectDropdown, {closeSelectDropdown} from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

function getDefaultMenuOptionDatas(schemas: Array<SchemaSelectorSchema> | null): Array<OptionData> {
  const optionDatas: Array<OptionData> = [];
  if (!schemas) {
    return optionDatas;
  }
  if (schemas.length === 0) {
    optionDatas.push({
      name: 'No schemas found',
    });
  } else {
    for (const schema of schemas) {
      optionDatas.push({
        name: getArchiveAwareDisplayName(schema),
        value: schema,
      });
    }
  }
  return optionDatas;
}

export const SCHEMA_SELECTOR_BASE_PROPS = {
  error: PropTypes.string,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired, // called with a schema shape
  schemas: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  selectedSchemaId: PropTypes.string,
};

export interface SchemaSelectorBaseProps {
  error?: string | null;
  isDisabled: boolean;
  onChange: (schema: SchemaSelectorSchema | null) => void;
  schemas: Array<SchemaSelectorSchema> | null;
  selectedSchemaId?: string | null;
  shouldDefaultIfSingleItem?: boolean;
  dataTestAttrs?: DataTestAttrs;
  /** Empty-state text shown when no schema is selected. Defaults to 'Select a schema...'. */
  placeholder?: string;
}

export interface SchemaSelectorProps extends SchemaSelectorBaseProps {
  itemContainerClassName?: string;
  menuRender?: (optionData: OptionData, shouldFade?: boolean) => JSX.Element;
  menuOptionDatas?: Array<OptionData>;
}

export interface SchemaSelectorSchema extends MaybeArchivableItem {}

// TODO(BNCH-2249): Deprecate SchemaSelector in favor of StarsAwareSchemaSelector
export default class SchemaSelector extends React.Component<SchemaSelectorProps> {
  static defaultProps = {
    isDisabled: false,
    dataTestAttrs: {component: 'SchemaSelector', element: 'selectDropdown'},
  };

  dropdown: SelectDropdown | null | undefined;

  private handleOnChange = (schema: SchemaSelectorSchema): void => {
    if (this.props.selectedSchemaId && this.props.selectedSchemaId === schema.id) {
      return;
    }
    this.props.onChange(schema);
    closeSelectDropdown(this.dropdown!);
  };

  private applyDefaultIfSingleItem = (): void => {
    if (
      this.props.selectedSchemaId == null &&
      this.props.shouldDefaultIfSingleItem &&
      this.props.schemas?.length === 1
    ) {
      this.handleOnChange(this.props.schemas[0]);
    }
  };

  override componentDidMount(): void {
    this.applyDefaultIfSingleItem();
  }

  override componentDidUpdate(): void {
    this.applyDefaultIfSingleItem();
  }

  override render(): JSX.Element {
    const {error, isDisabled, menuRender, menuOptionDatas, placeholder, schemas, selectedSchemaId} =
      this.props;
    const selectedSchema: SchemaSelectorSchema | undefined = schemas?.find(({id}) => id === selectedSchemaId);
    const optionDatas: Array<OptionData> = menuOptionDatas || getDefaultMenuOptionDatas(schemas);
    return (
      <SelectDropdown
        disabled={!schemas || isDisabled}
        className={isDisabled ? 'disabled--withBorder' : ''}
        displayName={
          selectedSchema ? getArchiveAwareDisplayName(selectedSchema) : (placeholder ?? 'Select a schema...')
        }
        error={error}
        menu={
          <SearchableMenu
            className="menu--short"
            scrollable={true} /* Schema selector is unnested so setting scrollable here is ok */
            width={Menu.WIDTHS.FULL}
            onSelect={this.handleOnChange}
            optionDatas={optionDatas}
            renderOptionContent={menuRender}
            selectedValue={selectedSchema}
            itemContainerClassName={this.props.itemContainerClassName}
          />
        }
        ref={(node) => {
          this.dropdown = node;
        }}
        dataTestAttrs={this.props.dataTestAttrs}
      />
    );
  }
}
